import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, TrendingDown, DollarSign, Calendar, ShoppingBag, Users } from 'lucide-react';

interface KPIProps {
    revenue: number;
    revenueVariation: number;
    bookings: number;
    bookingsVariation: number;
    ticket: number;
    ticketVariation: number;
    clients: number;
    clientsVariation: number;
}

const AnalyticsKPIs: React.FC<KPIProps> = ({
    revenue, revenueVariation,
    bookings, bookingsVariation,
    ticket, ticketVariation,
    clients, clientsVariation
}) => {

    const formatCurrency = (val: number) => `€ ${val.toLocaleString('it-IT', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

    const KPI_CARDS = [
        {
            label: "Fatturato Totale",
            value: formatCurrency(revenue),
            change: `${revenueVariation > 0 ? '+' : ''}${Math.round(revenueVariation)}%`,
            trend: revenueVariation >= 0 ? 'up' : 'down',
            icon: DollarSign,
            color: 'bg-green-50 text-green-600'
        },
        {
            label: "Appuntamenti",
            value: bookings.toString(),
            change: `${bookingsVariation > 0 ? '+' : ''}${Math.round(bookingsVariation)}%`,
            trend: bookingsVariation >= 0 ? 'up' : 'down',
            icon: Calendar,
            color: 'bg-blue-50 text-blue-600'
        },
        {
            label: "Scontrino Medio",
            value: formatCurrency(ticket),
            change: `${ticketVariation > 0 ? '+' : ''}${Math.round(ticketVariation)}%`,
            trend: ticketVariation >= 0 ? 'up' : 'down',
            icon: ShoppingBag,
            color: 'bg-orange-50 text-orange-600'
        },
        {
            label: "Nuovi Clienti",
            value: clients.toString(),
            change: `${clientsVariation > 0 ? '+' : ''}${Math.round(clientsVariation)}%`,
            trend: clientsVariation >= 0 ? 'up' : 'down',
            icon: Users,
            color: 'bg-purple-50 text-purple-600'
        },
    ];

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: { y: 0, opacity: 1 }
    };

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {KPI_CARDS.map((kpi, index) => (
                <motion.div key={index} variants={itemVariants} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 hover:shadow-md transition-all">
                    <div className="flex items-center justify-between mb-4">
                        <div className={`p-3 rounded-2xl ${kpi.color}`}>
                            <kpi.icon size={24} />
                        </div>
                        {revenueVariation !== 0 && <span className={`flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-full ${kpi.trend === 'up' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            {kpi.trend === 'up' ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                            {kpi.change}
                        </span>}
                    </div>
                    <h3 className="text-gray-500 text-sm font-medium">{kpi.label}</h3>
                    <p className="text-3xl font-bold text-gray-900 mt-1">{kpi.value}</p>
                </motion.div>
            ))}
        </div>
    );
};

export default AnalyticsKPIs;